import type { FilterDefinition, FilterOption, FilterState } from '#types/components/cms/event-collection-filters'
import type { EventCollectionItem } from '#types/components/cms/event-collection-item'
import type { LocaleCode } from '#types/locale'
import { getFormattedDate } from '#commerce/utils/date/getFormattedDate'
import { getMappedDateLocale } from '#core/utils/i18n'
import { normalizeEventCountryCode } from './eventCollectionProximity'

/**
 * Describes a date-based filter option used by the UTMB event tabs.
 * @category Utils
 */
export interface EventCollectionFilterOption {
  label: string
  value: string
}

/**
 * Describes a static filter group delivered by CMS for the event collection UI.
 * @category Utils
 */
export interface StaticEventFilterDefinition {
  label: string
  options?: string[]
}

/**
 * Defines the supported filter dimensions for the event collection.
 * @category Utils
 */
export const eventCollectionFilterKeys = ['countries', 'eventTypes', 'eventStatuses'] as const

/**
 * Represents the valid filter keys used by the event collection filter state.
 * @category Utils
 */
export type EventCollectionFilterKey = typeof eventCollectionFilterKeys[number]

/**
 * Creates an empty filter state for the event collection.
 * @returns A fresh filter state with all filter groups cleared.
 * @category Utils
 */
export const createEmptyEventCollectionFilterState = (): FilterState => ({
  countries: [],
  eventTypes: [],
  eventStatuses: [],
})

const isDefinedString = (value: string | undefined): value is string => Boolean(value)

type EventDateRange = {
  startDate: string
  endDate: string
}

const getUtcDate = (value: string): Date => new Date(`${value}T00:00:00Z`)

const getEventDateRange = (event: EventCollectionItem): EventDateRange | null => {
  const hasNormalizedDate = typeof event.date === 'object' && event.date !== null
  const startDate = hasNormalizedDate ? event.date.start : event.startDate
  const endDate = hasNormalizedDate ? (event.date.end || startDate) : (event.endDate || startDate)
  if (!startDate) return null

  return { startDate, endDate }
}

const getEventDateRangeValues = (event: EventCollectionItem): string[] => {
  const dateRange = getEventDateRange(event)
  if (!dateRange) return []

  const { startDate, endDate } = dateRange

  const start = getUtcDate(startDate)
  const end = getUtcDate(endDate)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return []

  const values: string[] = []
  const dayCount = Math.round((end.getTime() - start.getTime()) / 86400000)

  for (let offset = 0; offset <= dayCount; offset += 1) {
    const current = new Date(start)
    current.setUTCDate(current.getUTCDate() + offset)
    values.push(current.toISOString().slice(0, 10))
  }

  return values
}

/**
 * Formats a YYYY-MM-DD value as a compact UTMB filter label.
 * @param value - Date value in YYYY-MM-DD format.
 * @param locale - Active locale used to format the day and month portion.
 * @param translateWeekday - Weekday translation resolver keyed by lowercase weekday names.
 * @returns Label in the requested dd/mm format.
 * @category Utils
 */
export const createUTMBDateFilterLabel = (
  value: string,
  locale: string = 'en-gb',
  translateWeekday: (weekdayKey: string) => string = () => '',
): string => {
  const date = getUtcDate(value)
  const mappedLocale = getMappedDateLocale(locale as LocaleCode)
  const englishWeekday = new Intl.DateTimeFormat('en', { weekday: 'long', timeZone: 'UTC' }).format(date).toLowerCase()
  const localizedWeekday = new Intl.DateTimeFormat(mappedLocale, { weekday: 'long', timeZone: 'UTC' }).format(date)
  const dayLabel = translateWeekday(englishWeekday) || localizedWeekday
  const dayAndMonth = getFormattedDate(date, locale as LocaleCode, false, true).replace(/\/\d{4}$/, '')

  return `${dayLabel} ${dayAndMonth}`
}

/**
 * Builds date-based filter options for the UTMB event tabs.
 * @param events - Event cards available in the collection.
 * @param locale - Active locale used to format the day labels.
 * @param translateWeekday - Weekday translation resolver keyed by lowercase weekday names.
 * @returns Filter options for each day covered by the events.
 * @category Utils
 */
export const createUTMBDateFilterOptions = (
  events: EventCollectionItem[],
  locale: string = 'en-gb',
  translateWeekday: (weekdayKey: string) => string = () => '',
): EventCollectionFilterOption[] => {
  const eventDateValues = events.flatMap((event) => getEventDateRangeValues(event))

  return [...new Set(eventDateValues)]
    .sort()
    .map((value) => ({
      value,
      label: createUTMBDateFilterLabel(value, locale, translateWeekday),
    }))
}

/**
 * Resolves whether an event should appear for a selected UTMB filter date.
 * @param event - Event card available in the collection.
 * @param value - Filter value for a single day.
 * @returns True when the event spans the selected day.
 * @category Utils
 */
export const isEventInUTMBDateFilter = (event: EventCollectionItem, value: string): boolean => {
  const dateRange = getEventDateRange(event)
  if (!dateRange) return false

  return dateRange.startDate <= value && value <= dateRange.endDate
}

const cloneFilterState = (filters: FilterState): FilterState => ({
  countries: [...filters.countries],
  eventTypes: [...filters.eventTypes],
  eventStatuses: [...filters.eventStatuses],
})

/**
 * Builds the filter engine used by the event collection and mobile filters modal.
 * @param params - Live event data, CMS filter labels, and optional user country.
 * @param params.events - Event cards available in the collection.
 * @param params.staticEventFilters - Static filter groups coming from CMS.
 * @param params.userCountryCode - Active shopper country code used for the initial filter.
 * @returns Helpers for resolving filter options, matching events, and default selection state.
 * @category Utils
 */
export const createEventCollectionFilterEngine = (
  {
    events,
    staticEventFilters,
    userCountryCode = '',
  }: {
    events: EventCollectionItem[]
    staticEventFilters: StaticEventFilterDefinition[]
    userCountryCode?: string
  },
) => {
  const [countriesFilter, eventTypesFilter, eventStatusesFilter] = staticEventFilters

  const countryOptions = [...new Set(
    events.flatMap(({ locations }) =>
      locations?.map(({ name }) => name).filter(isDefinedString) ?? []
    ),
  )].sort()

  const eventTypeOptions = [...new Set(
    events.map(({ eventType }) => eventType?.label).filter(isDefinedString),
  )].sort()

  const mappedCountryCode = normalizeEventCountryCode(userCountryCode)
  const countryCodeToName = new Map<string, string>()
  for (const { locations } of events) {
    for (const { countryCode, name } of locations ?? []) {
      const code = normalizeEventCountryCode(countryCode)
      if (code && name) countryCodeToName.set(code, name)
    }
  }

  const statusOptions = eventStatusesFilter?.options ?? []
  const [upcomingLabel = '', pastLabel = ''] = statusOptions

  const getEventEndDateTime = (event: EventCollectionItem): Date | null => {
    const dateRange = getEventDateRange(event)
    if (!dateRange) return null

    // CMS dates have no event timezone. Interpret times without an offset in UTC
    // so status does not depend on the shopper's device timezone. A date without
    // a time remains upcoming through its final calendar day.
    const endTime = event.endTime
    const iso = endTime
      ? `${dateRange.endDate}T${endTime}${/(?:Z|[+-]\d{2}:\d{2})$/i.test(endTime) ? '' : 'Z'}`
      : `${dateRange.endDate}T23:59:59.999Z`
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return null
    return date
  }

  const isPastEvent = (event: EventCollectionItem): boolean => {
    const date = getEventEndDateTime(event)
    if (!date) return false
    return date < new Date()
  }

  const getEventStatusForFilter = (event: EventCollectionItem) =>
    isPastEvent(event) ? pastLabel : upcomingLabel

  const matchesFilters = (
    event: EventCollectionItem,
    filters: FilterState,
  ) => {
    const countryMatch = !filters.countries.length
      || event.locations?.some((loc) => filters.countries.includes(loc.name))
    const typeMatch = !filters.eventTypes.length
      || filters.eventTypes.includes(event.eventType?.label || '')
    const statusMatch = !filters.eventStatuses.length
      || filters.eventStatuses.includes(getEventStatusForFilter(event))

    return !!countryMatch && typeMatch && statusMatch
  }

  const getMatchingEvents = (filters: FilterState) =>
    events.filter((event) => matchesFilters(event, filters))

  const getFiltersForOption = (
    filters: FilterState,
    dimension: EventCollectionFilterKey,
    value: string,
  ): FilterState => {
    const filtersForOption = cloneFilterState(filters)
    filtersForOption[dimension] = [value]
    return filtersForOption
  }

  const withEventCounts = (
    options: string[],
    dimension: EventCollectionFilterKey,
    filters: FilterState,
  ): FilterOption[] =>
    options.map((value) => ({
      label: `${value} (${getMatchingEvents(getFiltersForOption(filters, dimension, value)).length})`,
      value,
    }))

  const getEventFiltersForState = (filters: FilterState): FilterDefinition[] => [
    {
      label: countriesFilter?.label ?? '',
      options: withEventCounts(countryOptions, 'countries', filters),
    },
    {
      label: eventTypesFilter?.label ?? '',
      options: withEventCounts(eventTypeOptions, 'eventTypes', filters),
    },
    {
      label: eventStatusesFilter?.label ?? '',
      options: withEventCounts(statusOptions, 'eventStatuses', filters),
    },
  ]

  const hasEventsInUserCountry = mappedCountryCode && events.some((event) =>
    event.locations?.some((loc) => normalizeEventCountryCode(loc.countryCode) === mappedCountryCode),
  )

  const userCountryName = countryCodeToName.get(mappedCountryCode)
  const initialCountrySelection = userCountryName
    && countryOptions.includes(userCountryName)
    && hasEventsInUserCountry
    ? [userCountryName]
    : []

  return {
    hasEventsInUserCountry,
    initialCountrySelection,
    isPastEvent,
    matchesFilters,
    getMatchingEvents,
    getEventFiltersForState,
  }
}
